import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { Sparkles, TrendingDown, TrendingUp, Equal } from "lucide-react";
import { AI_POSSESSIVE } from "../lib/brand";
import { formatMoney } from "../lib/currencyDisplay";

/**
 * Public price insight widget — shows host price vs AI recommended range.
 * Server-cached 24h per item, so this is cheap to display on every detail page.
 */
export default function PriceInsight({ kind, itemId, unit = "/day" }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!itemId) return;
    const path = kind === "vehicle"
      ? `${API}/vehicles/${itemId}/price-insight`
      : `${API}/parking/${itemId}/price-insight`;
    axios.get(path).then(r => setData(r.data)).catch(() => setError(true));
  }, [kind, itemId]);

  if (error || !data) return null;

  const { host_price, suggested_rate, low, high, rationale, verdict, currency = "USD" } = data;
  const money = (amount) => formatMoney(amount, currency);

  const defaultRationale = kind === "parking"
    ? "Market estimate based on space type, amenities, and location."
    : "Market estimate based on vehicle type and location.";

  const verdictMeta = {
    below: {
      label: "Below market",
      sub: "Great deal",
      color: "var(--zc-success)",
      bg: "rgba(22, 163, 74, 0.08)",
      icon: <TrendingDown size={14} />,
    },
    fair: {
      label: "Fair price",
      sub: "Within market range",
      color: "var(--zc-secondary)",
      bg: "var(--zc-secondary-soft)",
      icon: <Equal size={14} />,
    },
    above: {
      label: "Above market",
      sub: "Priced premium",
      color: "var(--zc-text-2)",
      bg: "var(--zc-bg-3)",
      icon: <TrendingUp size={14} />,
    },
  }[verdict] || { label: "Market price", sub: "", color: "var(--zc-text-2)", bg: "var(--zc-bg-3)", icon: <Equal size={14} /> };

  // Range bar — host_price position relative to (low * 0.85, high * 1.15)
  const visMin = low * 0.85;
  const visMax = high * 1.15;
  const span = Math.max(1, visMax - visMin);
  const lowPct = Math.max(0, Math.min(100, ((low - visMin) / span) * 100));
  const highPct = Math.max(0, Math.min(100, ((high - visMin) / span) * 100));
  const hostPct = Math.max(0, Math.min(100, ((host_price - visMin) / span) * 100));

  return (
    <div className="zc-card p-5" style={{ background: "linear-gradient(180deg, var(--zc-bg-2) 0%, " + verdictMeta.bg + " 100%)" }} data-testid={`price-insight-${kind}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-[var(--zc-accent)]" />
          <div className="eyebrow">{AI_POSSESSIVE} price insight</div>
        </div>
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-caption font-bold rounded-full"
          style={{ background: verdictMeta.bg, color: verdictMeta.color }}
          data-testid={`price-insight-verdict-${verdict}`}
        >
          {verdictMeta.icon} {verdictMeta.label}
        </span>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div>
          <div className="eyebrow">Host price</div>
          <div className="zc-price text-2xl mt-0.5">{money(host_price)}<span className="zc-unit normal-case tracking-normal">{unit}</span></div>
        </div>
        <div>
          <div className="eyebrow">{AI_POSSESSIVE} median</div>
          <div className="zc-stat text-2xl mt-0.5" style={{ color: verdictMeta.color }}>
            {money(suggested_rate)}<span className="zc-unit normal-case tracking-normal">{unit}</span>
          </div>
        </div>
      </div>

      {/* Range bar */}
      <div className="mt-4">
        <div className="relative h-1.5 bg-[var(--zc-bg-3)] rounded-full">
          <div
            className="absolute top-0 h-full rounded-full"
            style={{ left: `${lowPct}%`, width: `${highPct - lowPct}%`, background: "var(--zc-secondary)", opacity: 0.35 }}
          />
          {/* host price marker */}
          <div
            className="absolute -top-1 w-3 h-3.5 rounded-full border-2 border-[var(--zc-bg-2)]"
            style={{ left: `calc(${hostPct}% - 6px)`, background: verdictMeta.color, boxShadow: "0 1px 4px rgba(0,0,0,0.2)" }}
            title={`Host: ${money(host_price)}`}
          />
        </div>
        <div className="flex justify-between zc-meta mt-1.5 font-mono">
          <span>{money(low)}{unit}</span>
          <span>typical range</span>
          <span>{money(high)}{unit}</span>
        </div>
      </div>

      <p className="zc-hint text-[var(--zc-text-2)] mt-3 leading-relaxed" data-testid="price-insight-rationale">
        {rationale || defaultRationale}
      </p>
    </div>
  );
}
